import { storageService } from "@/lib/storage";
import { patrolApi } from "@/features/patrol/api";
import { queryClient } from "@/shared/queryClient";
import { ApiError } from "@/shared/apiClient";
import { logger } from "@/shared/logger";

const OUTBOX_STORAGE_KEY = "patrol.scan_outbox.v1";

export const PATROL_OUTBOX_FLUSH_INTERVAL_MS = 3 * 60 * 1000;

export type PatrolScanOutboxItem = {
  id: string;
  payload: string;
  shiftId?: number;
  clientRecordedAt: string;
  windowStart?: string;
  windowEnd?: string;
};

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export async function loadPatrolScanOutbox(): Promise<PatrolScanOutboxItem[]> {
  const raw = await storageService.get<PatrolScanOutboxItem[]>(OUTBOX_STORAGE_KEY);
  if (!Array.isArray(raw)) return [];
  return raw;
}

async function savePatrolScanOutbox(items: PatrolScanOutboxItem[]): Promise<void> {
  await storageService.set(OUTBOX_STORAGE_KEY, items);
}

export async function clearPatrolScanOutbox(): Promise<void> {
  await storageService.remove(OUTBOX_STORAGE_KEY);
}

export async function enqueuePatrolScan(item: Omit<PatrolScanOutboxItem, "id">): Promise<PatrolScanOutboxItem> {
  const row: PatrolScanOutboxItem = { ...item, id: newId() };
  const existing = await loadPatrolScanOutbox();
  existing.push(row);
  await savePatrolScanOutbox(existing);
  logger.info("patrol.outbox.enqueued", {
    id: row.id,
    shiftId: row.shiftId,
    recordedAt: row.clientRecordedAt,
    queueSize: existing.length,
  });
  return row;
}

export async function removePatrolScanOutboxItem(id: string): Promise<void> {
  const existing = await loadPatrolScanOutbox();
  const next = existing.filter((x) => x.id !== id);
  await savePatrolScanOutbox(next);
  logger.info("patrol.outbox.removed", {
    id,
    before: existing.length,
    after: next.length,
  });
}

/**
 * Unique payloads in the outbox whose clientRecordedAt falls within [windowStartIso, windowEndIso] inclusive by time.
 * Best-effort mirror of server "unique per period" until sync confirms.
 */
export function countUniquePendingInWindow(
  items: PatrolScanOutboxItem[],
  windowStartIso: string,
  windowEndIso: string
): number {
  const w0 = new Date(windowStartIso).getTime();
  const w1 = new Date(windowEndIso).getTime();
  if (Number.isNaN(w0) || Number.isNaN(w1) || w1 < w0) return 0;
  const seen = new Set<string>();
  for (const it of items) {
    const t = new Date(it.clientRecordedAt).getTime();
    if (Number.isNaN(t)) continue;
    if (t < w0 || t > w1) continue;
    const p = it.payload.trim();
    if (!p) continue;
    seen.add(p);
  }
  return seen.size;
}

let flushInFlight = false;

/**
 * Sends queued patrol scans in order. Removes items on success or on non-retryable client errors (404, 409).
 */
export async function flushPatrolScanOutbox(): Promise<void> {
  if (flushInFlight) return;
  flushInFlight = true;
  logger.info("patrol.outbox.flush.start");
  try {
    while (true) {
      const items = await loadPatrolScanOutbox();
      if (items.length === 0) break;
      const head = items[0];
      logger.info("patrol.outbox.flush.attempt", {
        id: head.id,
        shiftId: head.shiftId,
      });
      try {
        await patrolApi.postPatrolScan(head.payload, head.shiftId);
        logger.info("patrol.outbox.flush.success", { id: head.id });
        await removePatrolScanOutboxItem(head.id);
        queryClient.invalidateQueries({ queryKey: ["patrol", "config"] });
        queryClient.invalidateQueries({ queryKey: ["patrol", "status"] });
        queryClient.invalidateQueries({ queryKey: ["patrol", "outbox"] });
      } catch (err) {
        logger.warn("patrol.outbox.flush.error", { id: head.id, error: err });
        if (err instanceof ApiError) {
          if (err.status === 401) {
            logger.warn("patrol.outbox.flush.stopOn401", { id: head.id });
            break;
          }
          if (err.status === 404 || err.status === 409) {
            logger.warn("patrol.outbox.flush.dropNonRetryable", {
              id: head.id,
              status: err.status,
            });
            await removePatrolScanOutboxItem(head.id);
            queryClient.invalidateQueries({ queryKey: ["patrol", "outbox"] });
            continue;
          }
        }
        break;
      }
    }
  } finally {
    flushInFlight = false;
    logger.info("patrol.outbox.flush.end");
  }
}
